import { useMemo, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { EmployeeAvatar } from '@/components/Shared/EmployeeAvatar';
import { UtilizationBar } from '@/components/Shared/UtilizationBar';
import { usePlanningAssistantStore } from '@/store/usePlanningAssistantStore';
import {
  employeeById,
  employeeTone,
  fitsForTask,
  fmtHours,
  fmtRange,
  isAvailable,
  objectMap,
  taskById,
} from './assistantFormat';
import type { SuggestionsDto } from './assistantTypes';
import { AssistantMap } from './components/AssistantMap';
import { DueTag } from './components/DueTag';
import { ObjectTag } from './components/ObjectTag';
import { ReasonChips } from './components/ReasonChips';
import { ScorePill } from './components/ScorePill';

interface Props {
  data: SuggestionsDto;
}

export function MapMode({ data }: Props) {
  const { t, i18n } = useTranslation('planning');
  const lang = i18n.language;
  const selectedTaskId = usePlanningAssistantStore((s) => s.selectedTaskId);
  const selectTask = usePlanningAssistantStore((s) => s.selectTask);
  const hiddenRouteEmployeeIds = usePlanningAssistantStore((s) => s.hiddenRouteEmployeeIds);
  const toggleRoute = usePlanningAssistantStore((s) => s.toggleRoute);
  const assign = usePlanningAssistantStore((s) => s.assign);
  const busy = usePlanningAssistantStore((s) => s.busy);
  const [hoverEmployeeId, setHoverEmployeeId] = useState<string | null>(null);
  const [selectedStop, setSelectedStop] = useState<{ employeeId: string; planningEntryTaskId: string } | null>(
    null,
  );

  const objects = useMemo(() => objectMap(data), [data]);
  const available = (data.employees ?? []).filter(isAvailable);
  const tasks = data.tasks ?? [];
  const task = taskById(data, selectedTaskId);
  const top = task ? fitsForTask(data, task.taskId ?? '').slice(0, 3) : [];
  const ghostFit = top.find((f) => f.employeeId === hoverEmployeeId) ?? top[0] ?? null;
  const ghostEmployee = employeeById(data, ghostFit?.employeeId);

  const routes = available
    .filter((e) => !hiddenRouteEmployeeIds.includes(e.employeeId ?? ''))
    .map((employee) => ({
      employee,
      dim: task ? !top.some((f) => f.employeeId === employee.employeeId) : false,
    }));
  const markers = tasks.map((x) => ({ task: x, faded: !!task && x.taskId !== task.taskId }));

  // An open task takes precedence; the stop popover only shows while no task is selected.
  const stopEmployee = !task && selectedStop ? employeeById(data, selectedStop.employeeId) : undefined;
  const stopBlock = stopEmployee?.blocks?.find((b) => b.planningEntryTaskId === selectedStop?.planningEntryTaskId);
  const stop = stopEmployee && stopBlock ? { employee: stopEmployee, block: stopBlock } : null;

  return (
    <div className="grid gap-4 lg:h-full min-h-[560px] grid-cols-1 md:grid-cols-[220px_minmax(0,1fr)] lg:grid-cols-[260px_minmax(0,1fr)]">
      {/* Left: routes */}
      <div className="bg-white border border-slate-200 rounded-xl flex flex-col min-h-0">
        <div className="px-4 pt-4 pb-2">
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-400">
            {t('assistant.mapMode.kicker')}
          </div>
          <div className="text-[12px] text-slate-500 mt-0.5">{t('assistant.mapMode.hint')}</div>
        </div>
        <div className="flex-1 overflow-y-auto px-2">
          {available.map((e) => {
            const off = hiddenRouteEmployeeIds.includes(e.employeeId ?? '');
            const free = e.freeMinutes ?? 0;
            return (
              <label
                key={e.employeeId}
                className={`flex items-center gap-2.5 px-2 py-2 rounded-lg cursor-pointer hover:bg-slate-50 ${off ? 'opacity-45' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={!off}
                  onChange={() => toggleRoute(e.employeeId ?? '')}
                  className="accent-emerald-600"
                  aria-label={t('assistant.mapMode.showRoute', { name: e.name ?? '' })}
                />
                <span className="w-3 h-1 rounded-full shrink-0" style={{ background: employeeTone(e) }} />
                <span className="flex-1 min-w-0">
                  <span className="block text-[12.5px] text-slate-800 truncate">{e.name}</span>
                  <UtilizationBar planned={e.plannedMinutes ?? 0} target={e.targetMinutes ?? 0} height={3} />
                </span>
                <span className={`text-[11px] tabular-nums ${free >= 60 ? 'text-orange-600' : 'text-slate-400'}`}>
                  {free >= 15 ? fmtHours(free, lang) : t('assistant.full')}
                </span>
              </label>
            );
          })}
        </div>
        <div className="p-3 border-t border-slate-100 flex flex-col gap-1.5 text-[11px] text-slate-500">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full border-2 border-slate-500 bg-white shrink-0" />
            <span className="tabular-nums">{t('assistant.mapMode.legend.open', { count: tasks.length })}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-slate-500 border-2 border-white shrink-0" />
            {t('assistant.mapMode.legend.planned')}
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded bg-slate-500 shrink-0" />
            {t('assistant.mapMode.legend.start')}
          </div>
        </div>
      </div>

      {/* Map + popover */}
      <div className="relative min-h-[420px]">
        <div className="absolute inset-0">
          <AssistantMap
            className="h-full"
            routes={routes}
            markers={markers}
            objects={objects}
            selectedTaskId={task?.taskId ?? null}
            onTaskClick={(taskId) => {
              setSelectedStop(null);
              selectTask(taskId === selectedTaskId ? null : taskId);
            }}
            selectedStopId={stop?.block.planningEntryTaskId ?? null}
            onStopClick={(employeeId, planningEntryTaskId) => {
              selectTask(null);
              setSelectedStop(
                stop?.block.planningEntryTaskId === planningEntryTaskId ? null : { employeeId, planningEntryTaskId },
              );
            }}
            ghost={task && ghostFit && ghostEmployee ? { fit: ghostFit, task, employee: ghostEmployee } : null}
          />
        </div>
        {stop && (
          <div className="absolute top-3 right-3 z-[1000] w-[340px] max-w-[calc(100%-24px)] bg-white border border-slate-200 rounded-xl flex flex-col max-h-[calc(100%-24px)] overflow-y-auto shadow-[0_10px_30px_-10px_rgba(15,23,42,0.15)]">
            <div className="p-3.5 flex items-start gap-2 border-b border-slate-100">
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-400">
                  {t('assistant.mapMode.alreadyPlanned')}
                </div>
                <div className="text-[13.5px] font-semibold text-slate-900 mt-0.5">{stop.block.taskName}</div>
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  <ObjectTag
                    objectId={stop.block.objectId}
                    objectName={stop.block.objectName}
                    label={stop.block.objectShortName}
                  />
                  <span className="text-[11px] text-slate-500 tabular-nums">
                    {fmtRange(stop.block.start, stop.block.end)}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedStop(null)}
                aria-label={t('assistant.mapMode.close')}
                className="w-7 h-7 grid place-items-center rounded-md text-slate-400 hover:bg-slate-50"
              >
                <X size={14} strokeWidth={2} />
              </button>
            </div>
            <div className="p-3.5 flex items-center gap-2">
              <EmployeeAvatar
                employee={{ name: stop.employee.name, avatarColor: stop.employee.avatarColor }}
                size={28}
              />
              <span className="text-[12.5px] font-medium text-slate-900 flex-1 truncate">{stop.employee.name}</span>
            </div>
          </div>
        )}
        {task && (
          <div className="absolute top-3 right-3 z-[1000] w-[340px] max-w-[calc(100%-24px)] bg-white border border-slate-200 rounded-xl flex flex-col max-h-[calc(100%-24px)] overflow-y-auto shadow-[0_10px_30px_-10px_rgba(15,23,42,0.15)]">
            <div className="p-3.5 flex items-start gap-2 border-b border-slate-100">
              <div className="flex-1 min-w-0">
                <div className="text-[13.5px] font-semibold text-slate-900">{task.name}</div>
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  <ObjectTag objectId={task.objectId} objectName={task.objectName} label={task.objectShortName} />
                  <span className="text-[11px] text-slate-500 tabular-nums">{fmtHours(task.durationMinutes, lang)}</span>
                  <DueTag due={task.dueCategory} />
                </div>
              </div>
              <button
                type="button"
                onClick={() => selectTask(null)}
                aria-label={t('assistant.mapMode.close')}
                className="w-7 h-7 grid place-items-center rounded-md text-slate-400 hover:bg-slate-50"
              >
                <X size={14} strokeWidth={2} />
              </button>
            </div>
            {top.length === 0 && <div className="p-4 text-[12px] text-slate-500">{t('assistant.mapMode.nobody')}</div>}
            {top.map((f, i) => {
              const employee = employeeById(data, f.employeeId);
              if (!employee) return null;
              const isGhost = ghostFit?.employeeId === f.employeeId;
              return (
                <div
                  key={f.employeeId}
                  onMouseEnter={() => setHoverEmployeeId(f.employeeId ?? null)}
                  onMouseLeave={() => setHoverEmployeeId(null)}
                  className={`p-3.5 flex flex-col gap-2 border-b border-slate-100 ${isGhost ? 'bg-slate-50' : ''}`}
                >
                  <div className="flex items-center gap-2">
                    <EmployeeAvatar employee={{ name: employee.name, avatarColor: employee.avatarColor }} size={28} />
                    <span className="text-[12.5px] font-medium text-slate-900 flex-1 truncate">{employee.name}</span>
                    <ScorePill score={f.score} />
                  </div>
                  <ReasonChips reasons={f.reasons} objects={objects} startSource={employee.startPoint?.source} />
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void assign(f)}
                    className={`self-start whitespace-nowrap inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[12px] font-medium disabled:opacity-50 disabled:cursor-not-allowed ${
                      i === 0
                        ? 'bg-[#00A871] text-white hover:bg-emerald-700'
                        : 'border border-slate-200 text-slate-700 hover:bg-white'
                    }`}
                  >
                    <Plus size={13} strokeWidth={2} />
                    {t('assistant.mapMode.toRoute')}
                    <span className="tabular-nums">{fmtRange(f.start, f.end)}</span>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
